/**
 * The action bar's one-line status (action-bar plan phase 4): why a session
 * bar is locked (D1), and the server's hotbar notices (refused edits, a grant
 * no slot took (D4), a grant that went to the saved bar). Pure text, so the
 * copy is tested without rendering.
 */
import type { HotbarMode, HotbarNotice } from '../../../../shared/hotbar/hotbarProtocol';
import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';
import { MAX_BARS, MAX_PAGES_PER_BAR } from '../../../../shared/hotbar/barLayout';

/** How long a notice stays on the bar. */
export const BAR_NOTICE_MS = 5000;

/** The persistent label of a locked session bar, or null for the player's own bar. */
export function lockLabel(mode: HotbarMode | undefined): string | null {
  if (mode === 'peaceful') return 'Peaceful kit — your bar comes back when you leave peaceful mode';
  if (mode === 'race') return 'Race kit — your bar comes back when the race ends';
  return null;
}

const abilityName = (id: string) => getAbilityData(id)?.name ?? id;

export function noticeText(notice: HotbarNotice): string {
  switch (notice.kind) {
  case 'rejected':
    switch (notice.reason) {
    case 'session_bar': return lockLabel(notice.mode) ?? 'This bar can’t be changed right now';
    case 'untrained': return 'You haven’t learned that ability yet';
    case 'invalid_item': return 'Only usable items in your bag can go on the bar';
    case 'invalid_payload': return 'That change didn’t go through';
    case 'not_found': return 'That bar or page is gone';
    case 'bar_limit': return `You can have up to ${MAX_BARS} bars`;
    case 'page_limit': return `A bar can have up to ${MAX_PAGES_PER_BAR} pages`;
    case 'main_bar': return 'Your main bar always stays';
    case 'last_page': return 'A bar needs at least one page';
    }
    break;
  case 'not_placed':
    return notice.reason === 'bar_full'
      ? `Hotbar full — find ${abilityName(notice.abilityId)} in your spellbook (K)`
      : `${abilityName(notice.abilityId)}’s slot is in use — find it in your spellbook (K)`;
  case 'saved_bar':
    return `${abilityName(notice.abilityId)} went to your own bar, slot ${notice.slot}`;
  }
  return '';
}
